import type { CreateRedirectRequest } from '@shopexperts/shared';

/** Which page the editor is working on: nothing, an unsaved draft, or a stored page. */
export type PageEditorMode = 'empty' | 'new' | 'edit';

interface PathChangeState {
  mode: PageEditorMode;
  savedPath: string | null;
  draftPath: string;
}

/**
 * A redirect is only meaningful when a stored page moves to a different path:
 * a brand new page has no old URL to keep alive, and a blank draft path is a
 * half-typed edit rather than a move.
 */
export function shouldOfferRedirect(state: PathChangeState): boolean {
  if (state.mode !== 'edit') return false;
  const savedPath = state.savedPath?.trim();
  const draftPath = state.draftPath.trim();
  if (!savedPath || !draftPath) return false;
  return savedPath !== draftPath;
}

/** Permanent redirect from the page's previous path to the one being saved. */
export function buildPathChangeRedirect(state: PathChangeState): CreateRedirectRequest | null {
  if (!shouldOfferRedirect(state)) return null;
  const sourcePath = state.savedPath!.trim();
  if (!sourcePath.startsWith('/')) return null;

  return {
    source_path: sourcePath,
    target_path: state.draftPath.trim(),
    status_code: 301,
    is_active: true,
  };
}
